import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as productionActions from '../../actions/production';
import { withRouter } from 'react-router';
import { isMobile } from '../../constants/constants';
import { convertNumberToHMS } from '../Admin/globalFunctions';
import { axiosCaller } from '../../utils/apiCaller';
import { SelectOption } from '../../types/selectOption.type';
import { useUrlFilters } from '../../hooks/useUrlFilters';

import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaTable, { MikaColumn } from '../../components/MikaTable/MikaTable';
import MikaDateFilter, { DateRange } from '../../components/MikaDateFilter/MikaDateFilter';
import MikaExport, { ExportColumn } from '../../components/MikaExport/MikaExport';
import MikaViewToggle from '../../components/MikaViewToggle/MikaViewToggle';
import ContactDashboardStrip from '../Contact/ContactDashboardStrip';
import PaginationComponent from '../../components/Pagination/PaginationComponent';
import SearchablePopover from './SearchablePopover';
import ReturnHistoryDetail from './ReturnHistoryDetail';
import ReworkCardGrid from './ReworkCardGrid';
import '../../components/Pagination/Pagination.scss';
import '../Contact/Contact.scss';

interface Props {
    history: any;
    location: any;
    actions: any;
    productionActions: any;
    intl: any;
    permissions: any;
    buttonLoader: boolean;
    departments: SelectOption[];
    loading: any;
    companyProfile: any;
}

const VIEW_STORAGE_KEY = 'reworkViewMode';

const ReworkHistoryPage: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string, values?: Record<string, any>) =>
            intl.formatMessage({ id, defaultMessage }, values),
        [intl]
    );

    const { filters, setFilter, setFilters, replaceFilters } = useUrlFilters(props.history, props.location, {
        page: { default: 1, type: 'number' },
        perPage: { default: 20, type: 'number' },
        search: { default: '', type: 'string' },
        deptId: { default: 0, type: 'number' },
        dateFrom: { default: '', type: 'string' },
        dateTo: { default: '', type: 'string' },
        status: { default: '', type: 'string' },
        processId: { default: 0, type: 'number' },
        workerId: { default: 0, type: 'number' },
        returnedById: { default: 0, type: 'number' },
        processName: { default: '', type: 'string' },
        workerName: { default: '', type: 'string' },
        returnedByName: { default: '', type: 'string' },
    });

    // Derived variables
    const currentPage = filters.page;
    const perPage = filters.perPage;
    const searchTerm = filters.search;
    const selectedDepartmentId = filters.deptId || null;
    const dateRange: DateRange | null = filters.dateFrom && filters.dateTo ? { from: filters.dateFrom, to: filters.dateTo } : null;
    const statusFilter = filters.status || null;
    const activeProcessId = filters.processId || null;
    const activeProcessName = filters.processName;
    const activeWorkerId = filters.workerId || null;
    const activeWorkerName = filters.workerName;

    // Setter wrappers
    const setCurrentPage = (p: number) => setFilter('page', p);
    const setSelectedDepartmentId = (v: any) => setFilters({ deptId: v || 0, page: 1 });
    const setDateRange = (r: DateRange | null) => {
        setFilters({ dateFrom: r?.from || '', dateTo: r?.to || '', page: 1 });
    };

    const [viewMode, setViewMode] = useState<'table' | 'card'>(() => {
        return (localStorage.getItem(VIEW_STORAGE_KEY) as 'table' | 'card') || 'table';
    });

    const activeReturnedById = filters.returnedById || null;
    const activeReturnedByName = filters.returnedByName;

    // Dashboard
    const [statsData, setStatsData] = useState<any>(null);
    const [processPopoverOpen, setProcessPopoverOpen] = useState(false);
    const [workerPopoverOpen, setWorkerPopoverOpen] = useState(false);
    const [returnedByPopoverOpen, setReturnedByPopoverOpen] = useState(false);
    const [processPopoverPos, setProcessPopoverPos] = useState({ top: 0, left: 0 });
    const [workerPopoverPos, setWorkerPopoverPos] = useState({ top: 0, left: 0 });
    const [returnedByPopoverPos, setReturnedByPopoverPos] = useState({ top: 0, left: 0 });
    const processRef = useRef<HTMLDivElement>(null);
    const workerRef = useRef<HTMLDivElement>(null);
    const returnedByRef = useRef<HTMLDivElement>(null);
    const stripRef = useRef<HTMLDivElement>(null);

    // Data
    const [data, setData] = useState<any[]>([]);
    const [pagination, setPagination] = useState<any>(null);
    const [dataLoading, setDataLoading] = useState(false);

    // Detail modal
    const [detailRow, setDetailRow] = useState<any>(null);

    const loadStats = () => {
        axiosCaller('GET', 'rework-history/dashboard-stats').then((res: any) => {
            if (res?.data) setStatsData(res.data);
        }).catch(() => {});
    };

    useEffect(() => {
        props.actions.getAllDepartments();
        loadStats();
    }, []);

    useEffect(() => {
        fetchData();
    }, [props.location.search]);

    const fetchData = () => {
        setDataLoading(true);
        let params: string[] = [`page=${currentPage}`, `per_page=${perPage}`];
        if (selectedDepartmentId) params.push(`departmentId=${selectedDepartmentId}`);
        if (dateRange?.from) params.push(`startDate=${dateRange.from}`);
        if (dateRange?.to) params.push(`endDate=${dateRange.to}`);
        if (statusFilter) params.push(`status=${statusFilter}`);
        if (activeProcessId) params.push(`responsibilityId=${activeProcessId}`);
        if (activeWorkerId) params.push(`userId=${activeWorkerId}`);
        if (activeReturnedById) params.push(`returnedByUserId=${activeReturnedById}`);
        if (searchTerm) params.push(`search=${encodeURIComponent(searchTerm)}`);

        axiosCaller('GET', `rework-history?${params.join('&')}`).then((res: any) => {
            setData(res?.data?.data || []);
            const meta = res?.data?.meta;
            setPagination(meta ? {
                currentPage: meta.current_page,
                lastPage: meta.last_page,
                perPage: meta.per_page,
                total: meta.total,
            } : null);
            setDataLoading(false);
        }).catch(() => {
            setDataLoading(false);
        });
    };

    const handleSearch = (term: string) => {
        replaceFilters({ search: term, page: 1 });
    };

    // Dashboard strip
    const dashboardStats = useMemo(() => {
        const s = statsData;
        const avgSec = s?.avgResolutionSeconds || 0;
        const avgLabel = avgSec > 0 ? convertNumberToHMS(avgSec) : '\u2014';
        return [
            { key: 'total',         label: fm('production.rework.strip.totalReturns', 'Total kthimet'),     value: s?.total ?? 0,                 icon: 'alert' },
            { key: 'open',          label: fm('production.rework.strip.open', 'Të hapura'),                 value: s?.open ?? 0,                  icon: 'folder' },
            { key: 'resolved',      label: fm('production.rework.strip.resolved', 'Të zgjidhura'),          value: s?.resolved ?? 0,              icon: 'check' },
            { key: 'avgResolution', label: fm('production.responsibilityArchive.strip.avgTime', 'Koha mesatare'), value: avgLabel,                icon: 'check' },
            { key: 'processes',     label: fm('production.board.processes', 'Proceset'),                    value: (s?.processes || []).length,   icon: 'folder' },
            { key: 'workers',       label: fm('production.dashboard.workers', 'Punëtorët'),                 value: (s?.workers || []).length,     icon: 'agent' },
            { key: 'returnedBy',    label: fm('production.rework.strip.returnedBy', 'Kthyer nga'),          value: (s?.returnedBy || []).length,  icon: 'userPlus' },
        ];
    }, [statsData, fm]);

    const processes = useMemo(() => (statsData?.processes || []).map((p: any) => ({
        id: p.id, name: p.name, count: p.count, avgDuration: p.avgResolution || 0
    })), [statsData]);

    const workers = useMemo(() => (statsData?.workers || []).map((w: any) => ({
        id: w.id, name: w.name, count: w.count, avgDuration: w.avgResolution || 0
    })), [statsData]);

    const returnedByUsers = useMemo(() => (statsData?.returnedBy || []).map((r: any) => ({
        id: r.id, name: r.name, count: r.count, avgDuration: 0
    })), [statsData]);

    const getPopoverPos = (cardIndex: number) => {
        if (!stripRef.current) return { top: 0, left: 0 };
        const cards = stripRef.current.querySelectorAll('.cds-card');
        const card = cards[cardIndex];
        if (!card) return { top: 0, left: 0 };
        const stripRect = stripRef.current.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        return { top: cardRect.bottom - stripRect.top + 4, left: cardRect.left + cardRect.width / 2 - stripRect.left };
    };

    const closeAllPopovers = () => { setProcessPopoverOpen(false); setWorkerPopoverOpen(false); setReturnedByPopoverOpen(false); };

    const handleStripClick = (key: string) => {
        if (key === 'total') {
            setFilters({ status: '', page: 1 });
            closeAllPopovers();
        } else if (key === 'open') {
            setFilters({ status: statusFilter === 'open' ? '' : 'open', page: 1 });
            closeAllPopovers();
        } else if (key === 'resolved') {
            setFilters({ status: statusFilter === 'resolved' ? '' : 'resolved', page: 1 });
            closeAllPopovers();
        } else if (key === 'processes') {
            setWorkerPopoverOpen(false); setReturnedByPopoverOpen(false);
            if (!processPopoverOpen) setProcessPopoverPos(getPopoverPos(4));
            setProcessPopoverOpen(!processPopoverOpen);
        } else if (key === 'workers') {
            setProcessPopoverOpen(false); setReturnedByPopoverOpen(false);
            if (!workerPopoverOpen) setWorkerPopoverPos(getPopoverPos(5));
            setWorkerPopoverOpen(!workerPopoverOpen);
        } else if (key === 'returnedBy') {
            setProcessPopoverOpen(false); setWorkerPopoverOpen(false);
            if (!returnedByPopoverOpen) setReturnedByPopoverPos(getPopoverPos(6));
            setReturnedByPopoverOpen(!returnedByPopoverOpen);
        } else {
            closeAllPopovers();
        }
    };

    const handleProcessFilter = (p: { id: number; name: string }) => {
        setFilters({ processId: p.id, processName: p.name, workerId: 0, workerName: '', returnedById: 0, returnedByName: '', page: 1 });
        setProcessPopoverOpen(false);
    };

    const handleWorkerFilter = (w: { id: number; name: string }) => {
        setFilters({ workerId: w.id, workerName: w.name, processId: 0, processName: '', returnedById: 0, returnedByName: '', page: 1 });
        setWorkerPopoverOpen(false);
    };

    const handleReturnedByFilter = (r: { id: number; name: string }) => {
        setFilters({ returnedById: r.id, returnedByName: r.name, processId: 0, processName: '', workerId: 0, workerName: '', page: 1 });
        setReturnedByPopoverOpen(false);
    };

    const clearActiveFilter = () => {
        setFilters({ processId: 0, processName: '', workerId: 0, workerName: '', returnedById: 0, returnedByName: '', page: 1 });
    };

    // Close popovers on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (processRef.current && !processRef.current.contains(e.target as Node)) setProcessPopoverOpen(false);
            if (workerRef.current && !workerRef.current.contains(e.target as Node)) setWorkerPopoverOpen(false);
            if (returnedByRef.current && !returnedByRef.current.contains(e.target as Node)) setReturnedByPopoverOpen(false);
        };
        if (processPopoverOpen || workerPopoverOpen || returnedByPopoverOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [processPopoverOpen, workerPopoverOpen, returnedByPopoverOpen]);

    const handleViewChange = (mode: 'table' | 'card') => {
        setViewMode(mode);
        localStorage.setItem(VIEW_STORAGE_KEY, mode);
    };

    const formatResolution = (seconds: number): string => {
        if (!seconds) return '\u2014';
        const d = Math.floor(seconds / 86400);
        const h = Math.floor((seconds % 86400) / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        if (d > 0) return `${d}d ${h}h ${m}m ${s}s`;
        if (h > 0) return `${h}h ${m}m ${s}s`;
        if (m > 0) return `${m}m ${s}s`;
        return `${s}s`;
    };

    // Columns
    const columns: MikaColumn[] = useMemo(() => [
        { key: 'clientName',         title: fm('production.detail.client', 'Klienti'),                              sortable: true, render: (val: any) => val || '\u2014' },
        { key: 'projectName',        title: fm('production.detail.project', 'Projekti'),                            hideOnMobile: true, render: (val: any) => val || '\u2014' },
        { key: 'articleName',        title: fm('production.board.productCol', 'Produkti'),                          hideOnMobile: true, render: (val: any) => val || '\u2014' },
        { key: 'materialName',       title: fm('production.responsibilityArchive.col.material', 'Materiali'),       hideOnMobile: true, render: (val: any) => val || '\u2014' },
        { key: 'responsibilityName', title: fm('production.responsibilityArchive.col.process', 'Procesi'),          render: (val: any) => val || '\u2014' },
        { key: 'lastAssignedWorker', title: fm('production.responsibilityArchive.popover.worker', 'Punëtori'),      hideOnMobile: true, render: (val: any) => val || '\u2014' },
        { key: 'lastReturnedByUser', title: fm('production.rework.strip.returnedBy', 'Kthyer nga'),                 hideOnMobile: true, render: (val: any) => val || '\u2014' },
        {
            key: 'lastReturnReason', title: fm('production.rework.col.reason', 'Arsyeja'), hideOnMobile: true,
            render: (val: any) => val ? (
                <span title={val} style={{ display: 'block', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11, color: '#64748b' }}>
                    {val}
                </span>
            ) : <span style={{ color: '#cbd5e1' }}>{'\u2014'}</span>
        },
        {
            key: 'returnCount', title: fm('production.rework.col.returnCount', 'Kthimet'),
            render: (val: any, row: any) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {row.adminReturnCount > 0 && (
                        <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>{fm('production.responsibilityArchive.modal.returnedTimes', 'Kthyer {count} herë', { count: row.adminReturnCount })}</span>
                    )}
                    {row.workerBlockCount > 0 && (
                        <span style={{ fontSize: 11, color: '#f97316', fontWeight: 600 }}>{fm('production.responsibilityArchive.modal.blockedTimes', 'Bllokuar {count} herë', { count: row.workerBlockCount })}</span>
                    )}
                    {row.openCount > 0 && (
                        <span style={{ fontSize: 10, color: '#d97706' }}>{row.openCount} {fm('production.returnHistory.open', 'E hapur').toLowerCase()}</span>
                    )}
                </div>
            )
        },
        {
            key: 'lastCategoryName', title: fm('production.rework.col.category', 'Kategoria'), hideOnMobile: true,
            render: (val: any) => val ? (
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: '#dbeafe', color: '#1e40af', fontWeight: 600 }}>{val}</span>
            ) : <span style={{ color: '#cbd5e1' }}>{'\u2014'}</span>
        },
        {
            key: 'status', title: fm('production.products.column.status', 'Statusi'),
            render: (val: any) => (
                <span style={{
                    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                    padding: '2px 8px', borderRadius: 4,
                    color: val === 'open' ? '#92400e' : '#166534',
                    background: val === 'open' ? '#fef3c7' : '#dcfce7'
                }}>
                    {val === 'open' ? fm('production.returnHistory.open', 'E hapur') : fm('production.returnHistory.resolved', 'E zgjidhur')}
                </span>
            )
        },
        { key: 'lastReturnedAt', title: fm('production.rework.col.lastReturn', 'Kthimi i fundit'), hideOnMobile: true, render: (val: any) => val || '\u2014' },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale]);

    // Export
    const exportColumns: ExportColumn[] = useMemo(() => [
        { key: 'clientName',         title: fm('production.detail.client', 'Klienti') },
        { key: 'projectName',        title: fm('production.detail.project', 'Projekti') },
        { key: 'articleName',        title: fm('production.board.productCol', 'Produkti') },
        { key: 'materialName',       title: fm('production.responsibilityArchive.col.material', 'Materiali') },
        { key: 'responsibilityName', title: fm('production.responsibilityArchive.col.process', 'Procesi') },
        { key: 'lastAssignedWorker', title: fm('production.responsibilityArchive.popover.worker', 'Punëtori') },
        { key: 'lastReturnedByUser', title: fm('production.rework.strip.returnedBy', 'Kthyer nga') },
        { key: 'lastReturnReason',   title: fm('production.rework.col.reason', 'Arsyeja') },
        { key: 'returnCount',        title: fm('production.rework.col.returnCount', 'Kthimet') },
        { key: 'lastCategoryName',   title: fm('production.rework.col.category', 'Kategoria') },
        { key: 'status',             title: fm('production.products.column.status', 'Statusi') },
        { key: 'lastReturnedAt',     title: fm('production.rework.col.lastReturn', 'Kthimi i fundit') },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale]);

    const fetchAllForExport = async (): Promise<any[]> => {
        let params: string[] = ['per_page=10000'];
        if (selectedDepartmentId) params.push(`departmentId=${selectedDepartmentId}`);
        if (dateRange?.from) params.push(`startDate=${dateRange.from}`);
        if (dateRange?.to) params.push(`endDate=${dateRange.to}`);
        if (searchTerm) params.push(`search=${encodeURIComponent(searchTerm)}`);
        if (statusFilter) params.push(`status=${statusFilter}`);
        if (activeProcessId) params.push(`responsibilityId=${activeProcessId}`);
        if (activeWorkerId) params.push(`userId=${activeWorkerId}`);
        if (activeReturnedById) params.push(`returnedByUserId=${activeReturnedById}`);
        const res = await axiosCaller('GET', `rework-history?${params.join('&')}`);
        return (res?.data?.data || []).map((r: any) => ({
            ...r,
            status: r.status === 'open' ? fm('production.returnHistory.open', 'E hapur') : fm('production.returnHistory.resolved', 'E zgjidhur'),
        }));
    };

    return (
        <div className="clp-page">
            <MikaPageHeader title={fm('production.returnHistory.title', 'Historiku i kthimeve')} />

            {/* Department tabs */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0', overflowX: 'auto' }}>
                <button className={`clp-dept-tab ${selectedDepartmentId === null ? 'clp-dept-tab-active' : ''}`}
                    onClick={() => setFilters({ deptId: 0, page: 1 })} type="button">
                    <FormattedMessage id="common.pagination.all" defaultMessage="Të gjitha" />
                </button>
                {(props.departments || []).map((dept: any) => (
                    <button key={dept.value} className={`clp-dept-tab ${selectedDepartmentId === dept.value ? 'clp-dept-tab-active' : ''}`}
                        onClick={() => setFilters({ deptId: dept.value, page: 1 })} type="button">
                        {dept.label}
                    </button>
                ))}
            </div>

            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input type="text" className="clp-search-input" placeholder={fm('production.rework.searchPlaceholder', 'Kërko...')}
                        value={searchTerm} onChange={(e) => handleSearch(e.target.value)} />
                    {searchTerm && (
                        <button className="clp-search-clear" onClick={() => handleSearch('')} type="button">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>
                <div className="clp-toolbar-actions">
                    <MikaDateFilter value={dateRange} onChange={(r) => setDateRange(r)} />
                    {!isMobile && <MikaViewToggle mode={viewMode} onChange={handleViewChange} />}
                    <MikaExport fetchAllData={fetchAllForExport} columns={exportColumns} fileName={fm('production.rework.exportFilename', 'historiku_kthimeve')} title={fm('production.returnHistory.title', 'Historiku i kthimeve')} companyProfile={props.companyProfile} />
                </div>
            </div>

            <div ref={stripRef} style={{ position: 'relative' }}>
                    <ContactDashboardStrip stats={dashboardStats} onFilterClick={handleStripClick} activeFilter={statusFilter || 'total'} />

                    {processPopoverOpen && processes.length > 0 && (
                        <SearchablePopover
                            popoverRef={processRef} items={processes} label={fm('production.responsibilityArchive.popover.process', 'Procesi')}
                            searchPlaceholder={fm('production.responsibilityArchive.popover.processSearch', 'Kërko proces...')} activeId={activeProcessId}
                            onSelect={handleProcessFilter}
                            style={{ top: processPopoverPos.top, left: processPopoverPos.left, transform: 'translateX(-50%)' }}
                        />
                    )}

                    {workerPopoverOpen && workers.length > 0 && (
                        <SearchablePopover
                            popoverRef={workerRef} items={workers} label={fm('production.responsibilityArchive.popover.worker', 'Punëtori')}
                            searchPlaceholder={fm('production.responsibilityArchive.popover.workerSearch', 'Kërko punëtor...')} activeId={activeWorkerId}
                            onSelect={handleWorkerFilter} showRank
                            style={{ top: workerPopoverPos.top, left: workerPopoverPos.left, transform: 'translateX(-50%)' }}
                        />
                    )}

                    {returnedByPopoverOpen && returnedByUsers.length > 0 && (
                        <SearchablePopover
                            popoverRef={returnedByRef} items={returnedByUsers} label={fm('production.rework.popover.administrator', 'Administratori')}
                            searchPlaceholder={fm('production.rework.popover.administratorSearch', 'Kërko administrator...')} activeId={activeReturnedById}
                            onSelect={handleReturnedByFilter} showRank hideAvg
                            style={{ top: returnedByPopoverPos.top, left: returnedByPopoverPos.left, transform: 'translateX(-50%)' }}
                        />
                    )}
            </div>

            {/* Active filters */}
            {(activeProcessId || activeWorkerId || activeReturnedById) && (
                <div className="clp-active-filter-bar">
                    <div className="clp-active-filter-chip">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {activeWorkerId
                                ? <><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></>
                                : activeReturnedById
                                    ? <><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" /></>
                                    : <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />}
                        </svg>
                        <span>{activeWorkerId ? activeWorkerName : activeReturnedById ? activeReturnedByName : activeProcessName}</span>
                        <span className="clp-active-filter-count">{pagination?.total ?? 0}</span>
                        <button className="clp-active-filter-clear" onClick={clearActiveFilter} type="button">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    </div>
                </div>
            )}

            {(isMobile ? 'table' : viewMode) === 'table'
                ? <MikaTable
                    columns={columns}
                    data={data}
                    loading={dataLoading}
                    onRowClick={(row: any) => setDetailRow(row)}
                />
                : <ReworkCardGrid data={data} onRowClick={(row: any) => setDetailRow(row)} />
            }

            <PaginationComponent
                currentPage={currentPage} perPage={perPage} pagination={pagination}
                onPageChange={(p) => setFilter('page', p)} onPerPageChange={(v) => setFilters({ perPage: v, page: 1 })}
                label={fm('production.responsibilityArchive.recordsLabel', 'procese')}
            />

            {/* Detail modal */}
            {detailRow && (
                <ReturnHistoryDetail
                    productId={detailRow.productId}
                    responsibilityId={detailRow.responsibilityId}
                    materialId={detailRow.materialId}
                    title={`${detailRow.articleName || ''} — ${detailRow.responsibilityName || ''} ${detailRow.materialName ? '(' + detailRow.materialName + ')' : ''}`}
                    onClose={() => { setDetailRow(null); loadStats(); }}
                    permissions={props.permissions}
                />
            )}
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return { ...ownProps, ...state.app };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        productionActions: bindActionCreators(productionActions as any, dispatch),
        actions: bindActionCreators(actions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(ReworkHistoryPage as any)));
